import React, { useState } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Camera, X, Repeat } from "lucide-react";

export const TAG_STYLE = {
  sos: "bg-[#FEE2E2] text-[#991B1B]", next_day: "bg-[#FEF3C7] text-[#92400E]", winter: "bg-[#E0EEF6] text-[#1F5673]",
  tactical: "bg-[#EEF1EA] text-[#4F5B45]", recurring: "bg-[#F1EDE6] text-[#45423C]",
};
export const inp = "w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
export const lbl = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mb-1";

export const PeoplePicker = ({ people, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {people.map((p) => {
      const on = value.includes(p.id);
      return (
        <button type="button" key={p.id} data-testid={`${testid}-${p.id}`} onClick={() => onChange(on ? value.filter((x) => x !== p.id) : [...value, p.id])}
          className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#45423C] border-[#E3DED4]"}`}>
          {p.name}
        </button>
      );
    })}
  </div>
);

export const PhotoPicker = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        ids.push((await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.file_id);
      }
      onChange([...value, ...ids]);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 rounded-lg border border-[#E3DED4] px-4 py-2 text-sm cursor-pointer hover:border-[#B9AE9A] w-fit bg-white">
        <Camera className="h-4 w-4" /> {busy ? "Ανέβασμα…" : "Φωτογραφία"}
        <input data-testid={testid} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} disabled={busy} />
      </label>
      {value.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {value.map((fid) => (
            <div key={fid} className="relative">
              <img src={fileUrl(fid)} alt="" className="h-16 w-16 rounded-lg object-cover border border-[#E3DED4]" />
              <button type="button" onClick={() => onChange(value.filter((x) => x !== fid))} className="absolute -top-2 -right-2 bg-[#991B1B] text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TagPicker = ({ tags, units, form, setForm }) => {
  const toggle = (g) => setForm({ ...form, tags: form.tags.includes(g) ? form.tags.filter((x) => x !== g) : [...form.tags, g] });
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {tags.map((g) => (
          <button type="button" key={g.id} data-testid={`new-tag-${g.id}`} onClick={() => toggle(g.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tags.includes(g.id) ? `${TAG_STYLE[g.id]} border-transparent ring-2 ring-[#45423C]/30` : "bg-white border-[#E3DED4] text-[#6F6A60]"}`}>
            {g.name}
          </button>
        ))}
      </div>
      {form.tags.includes("recurring") && (
        <div className="flex items-center gap-2 mt-3 text-sm text-[#45423C]">
          <Repeat className="h-4 w-4" /> Κάθε
          <input data-testid="new-repeat-every" type="number" min="1" max="365" value={form.repeat_every}
            onChange={(e) => setForm({ ...form, repeat_every: Number(e.target.value) || 1 })} className={`${inp} !w-20`} />
          <select data-testid="new-repeat-unit" value={form.repeat_unit} onChange={(e) => setForm({ ...form, repeat_unit: e.target.value })} className={`${inp} !w-36`}>
            {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};
